import { StatusTabs, type StatusTab } from "./components/status-tabs"

const CONTENT_LINKS = [
  { key: "overview", to: "/admin/content", label: "Overview", end: true },
  { key: "newsletters", to: "/admin/newsletters", label: "Newsletters", end: false },
  { key: "subscribers", to: "/admin/subscribers", label: "Subscribers", end: false },
] as const satisfies readonly StatusTab[]

export function ContentNav() {
  return (
    <nav aria-label="Content sections">
      <StatusTabs tabs={CONTENT_LINKS} ariaLabel="Content sections" />
    </nav>
  )
}

export function ContentStatusBadge({ value }: { value: string }) {
  const colors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700",
    sent: "bg-green-100 text-green-700",
    subscribed: "bg-green-100 text-green-700",
    unsubscribed: "bg-gray-100 text-gray-600",
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${colors[value] ?? "bg-cream-100 text-ink/60"}`}>
      {value}
    </span>
  )
}

function pageList(page: number, pageCount: number): (number | "…")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1)

  const pages = [...new Set([1, 2, page - 1, page, page + 1, pageCount - 1, pageCount])]
    .filter((value) => value >= 1 && value <= pageCount)
    .sort((a, b) => a - b)
  const items: (number | "…")[] = []
  let previous = 0

  for (const value of pages) {
    if (previous && value - previous > 1) items.push("…")
    items.push(value)
    previous = value
  }

  return items
}

export function ContentPagination({
  page,
  pageSize,
  total,
  onPageChange,
  disabled = false,
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  disabled?: boolean
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  if (pageCount <= 1 && !disabled) return null

  const from = disabled ? 0 : (page - 1) * pageSize + 1
  const to = disabled ? 0 : Math.min(page * pageSize, total)

  const buttonClass =
    "rounded-lg border border-gold-500 bg-white px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-cream-100 disabled:pointer-events-none disabled:opacity-40"

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-ink/50">
      <span>
        {disabled ? "Loading..." : `Showing ${from}-${to} of ${total}`}
      </span>
      <div className="flex w-full flex-wrap items-center justify-end gap-1 sm:w-auto">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={disabled || page <= 1}
          className={buttonClass}
        >
          First
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= 1}
          className={buttonClass}
        >
          Prev
        </button>
        {pageList(page, pageCount).map((item, index) =>
          item === "…" ? (
            <span key={`gap-${index}`} className="px-1 text-xs text-ink/40" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              disabled={disabled}
              aria-current={item === page ? "page" : undefined}
              aria-label={`Page ${item}`}
              className={`min-w-8 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-40 ${
                item === page
                  ? "bg-forest-950 text-white"
                  : "text-ink hover:bg-cream-100"
              }`}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= pageCount}
          className={buttonClass}
        >
          Next
        </button>
        <button
          type="button"
          onClick={() => onPageChange(pageCount)}
          disabled={disabled || page >= pageCount}
          className={buttonClass}
        >
          Last
        </button>
      </div>
    </div>
  )
}

export function ContentEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-[10px] border border-gold-500 bg-white p-10 text-center">
      <p className="text-sm text-ink/50">{message}</p>
    </div>
  )
}
